/**
 * Hook for fetching and managing transaction history
 * Extracted from Activity.tsx to improve code organization
 */

import { useState, useCallback } from "react";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import type { TransactionCustomFieldValues } from "../../../types";

export interface TransactionHistoryEntry {
  id: string;
  userId: string;
  timestamp: Timestamp;
  changes: string[];
  attachmentUrl?: string;
  attachmentName?: string;
  customFieldValues?: TransactionCustomFieldValues;
}

export interface UseTransactionHistoryResult {
  transactionHistory: TransactionHistoryEntry[];
  loadingHistory: boolean;
  fetchTransactionHistory: (transactionId: string, workspaceId: string) => Promise<void>;
}

/**
 * Hook for fetching transaction history
 */
export const useTransactionHistory = (): UseTransactionHistoryResult => {
  const [transactionHistory, setTransactionHistory] = useState<TransactionHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

  const fetchTransactionHistory = useCallback(async (transactionId: string, workspaceId: string): Promise<void> => {
    if (!workspaceId) {
      console.log("Cannot fetch history: missing workspaceId");
      return;
    }

    if (!transactionId) {
      console.log("Cannot fetch history: missing transactionId");
      return;
    }

    setLoadingHistory(true);
    try {
      console.log("Fetching history for transaction:", transactionId);
      const historyRef = collection(db, "workspaces", workspaceId, "transactions", transactionId, "history");
      const q = query(historyRef, orderBy("timestamp", "desc"));
      const snapshot = await getDocs(q);
      
      console.log("History snapshot size:", snapshot.size);
      
      const historyEntries: TransactionHistoryEntry[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        console.log("History entry data:", data);
        return {
          id: docSnap.id,
          userId: data.userId,
          timestamp: data.timestamp as Timestamp,
          changes: data.changes || [],
          attachmentUrl: data.attachmentUrl as string | undefined,
          attachmentName: data.attachmentName as string | undefined,
          customFieldValues: data.customFieldValues as TransactionCustomFieldValues | undefined,
        };
      });

      console.log("History entries:", historyEntries);
      setTransactionHistory(historyEntries);
    } catch (error) {
      console.error("Error fetching transaction history:", error);
      console.error("Transaction ID:", transactionId);
      console.error("Workspace ID:", workspaceId);
      setTransactionHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  return {
    transactionHistory,
    loadingHistory,
    fetchTransactionHistory,
  };
};
